import { Conditions, Fields } from '@root/constants'
import { useBusinessContext } from '@root/context'
import { fetchContact, IAccountingRule, Rule } from '@root/query'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { FaBuilding, FaLock } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import {
  ReadOnlyContactBox,
  ReadOnlyContactField,
  ReadOnlyContactHelper,
  ReadOnlyContactLabelRow,
  ReadOnlyContactValue
} from '../AccountingRuleForm.styled'

const isRule = (rule: Rule | { operand?: string }): rule is Rule => 'field' in rule

const contactIdFromRules = (
  rules: Array<Rule | { operand?: string }> | undefined
): number | null => {
  for (const rule of rules ?? []) {
    if (!isRule(rule) || rule.field !== Fields.CONTACT) continue
    if (rule.condition !== Conditions.CONTACT_IS_IN || !Array.isArray(rule.value)) continue
    const id = rule.value.find((value): value is number => typeof value === 'number')
    if (id != null) return id
  }
  return null
}

const WorkflowContactField: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const { control } = useFormContext<IAccountingRule>()
  const rules = useWatch({ control, name: 'rule_node.rules' })
  const contactId = contactIdFromRules(rules)

  const { data: contact } = useQuery({
    queryKey: [slug, 'contacts', contactId],
    queryFn: () => fetchContact({ slug, contactId: contactId! }),
    enabled: contactId != null,
    staleTime: 60 * 1000
  })

  if (contactId == null) return null

  const contactName = contact?.name?.trim() || '…'

  return (
    <ReadOnlyContactField>
      <ReadOnlyContactBox>
        <ReadOnlyContactLabelRow>
          <span>{t('settings.automation.accountingRules.form.contactLabel')}</span>
          <FaLock aria-hidden />
        </ReadOnlyContactLabelRow>
        <ReadOnlyContactValue>
          <FaBuilding aria-hidden />
          {contactName}
        </ReadOnlyContactValue>
      </ReadOnlyContactBox>
      <ReadOnlyContactHelper>
        {t('settings.automation.accountingRules.form.contactFromWorkflow')}
      </ReadOnlyContactHelper>
    </ReadOnlyContactField>
  )
}

export default WorkflowContactField
